import { useState, useRef, useEffect, useCallback } from 'react';
import useWorldStore from '../store/worldStore';
import { orchestrateMasterChat } from '../services/openclawMasterChat';
import { runAgentQueue } from '../lib/taskQueueActions';
import { getErrorMessage, showErrorToast } from '../../../lib/toast-store';
import { MicIcon, MicOffIcon, SendIcon } from '@/components/icons';

const mono = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };

const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function findMentionedAgentId(message, agents) {
  const source = String(message || '');
  return [...agents]
    .filter((agent) => agent?.id && agent?.name)
    .sort((a, b) => b.name.length - a.name.length)
    .find((agent) =>
      new RegExp(`@${escapeRegExp(agent.name)}(?=$|[\\s,.;:!?])`, 'i').test(source)
    )
    ?.id || null;
}

export default function MasterChat() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [status, setStatus] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const inputRef = useRef();
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);

  const agents = useWorldStore((s) => s.agents);
  const accessToken = useWorldStore((s) => s.accessToken);
  const voiceMode = useWorldStore((s) => s.voiceMode);
  const toggleVoiceMode = useWorldStore((s) => s.toggleVoiceMode);

  const flashStatus = useCallback((text) => {
    setStatus(text);
    setTimeout(() => setStatus(null), 3000);
  }, []);

  // Voice recognition setup
  useEffect(() => {
    if (!SpeechRecognition) return;
    if (voiceMode !== 'open') {
      // Stop if switching away from open
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => {
      setIsListening(false);
      setIsSpeaking(false);
      // Restart if still in open mode
      if (useWorldStore.getState().voiceMode === 'open') {
        try {
          recognition.start();
        } catch (error) {
          flashStatus(`Speech recognition restart failed: ${error.message || String(error)}`);
        }
      }
    };

    recognition.onsoundstart = () => setIsSpeaking(true);
    recognition.onsoundend = () => setIsSpeaking(false);

    recognition.onresult = (event) => {
      let transcript = '';
      let isFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
        if (event.results[i].isFinal) isFinal = true;
      }

      if (transcript.trim()) {
        setInput(transcript.trim());
        setIsSpeaking(true);

        // Auto-submit after 1.5s of silence on final result
        clearTimeout(silenceTimerRef.current);
        if (isFinal) {
          silenceTimerRef.current = setTimeout(() => {
            setIsSpeaking(false);
            // Trigger send
            const submitBtn = document.querySelector('[data-voice-submit]');
            if (submitBtn) submitBtn.click();
          }, 1500);
        }
      }
    };

    recognition.onerror = (event) => {
      flashStatus(`Speech recognition error: ${event.error || 'Unknown speech recognition error'}`);
    };

    try {
      recognition.start();
    } catch (error) {
      window.setTimeout(() => {
        flashStatus(`Speech recognition start failed: ${error.message || String(error)}`);
      }, 0);
    }

    return () => {
      clearTimeout(silenceTimerRef.current);
      try {
        recognition.stop();
      } catch (error) {
        flashStatus(`Speech recognition stop failed: ${error.message || String(error)}`);
      }
    };
  }, [voiceMode, flashStatus]);

  // Spacebar hold-to-talk
  useEffect(() => {
    if (!SpeechRecognition) return;
    let spaceRecognition = null;
    let submitTimer = null;

    const onKeyDown = (e) => {
      if (e.code !== 'Space' || e.repeat) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();

      spaceRecognition = new SpeechRecognition();
      spaceRecognition.continuous = true;
      spaceRecognition.interimResults = true;
      spaceRecognition.lang = 'en-US';

      spaceRecognition.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        if (transcript.trim()) {
          setInput(transcript.trim());
          setIsSpeaking(true);
        }
      };

      spaceRecognition.onsoundstart = () => setIsSpeaking(true);
      spaceRecognition.onsoundend = () => setIsSpeaking(false);
      spaceRecognition.onerror = (event) => {
        flashStatus(`Speech recognition error: ${event.error || 'Unknown speech recognition error'}`);
      };

      setIsListening(true);
      setIsSpeaking(false);
      try {
        spaceRecognition.start();
      } catch (error) {
        window.setTimeout(() => {
          flashStatus(`Speech recognition start failed: ${error.message || String(error)}`);
        }, 0);
      }
    };

    const onKeyUp = (e) => {
      if (e.code !== 'Space') return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (spaceRecognition) {
        try {
          spaceRecognition.stop();
        } catch (error) {
          flashStatus(`Speech recognition stop failed: ${error.message || String(error)}`);
        }
        spaceRecognition = null;
      }
      setIsListening(false);
      setIsSpeaking(false);

      // Auto-submit after 500ms
      clearTimeout(submitTimer);
      submitTimer = setTimeout(() => {
        const btn = document.querySelector('[data-voice-submit]');
        if (btn) btn.click();
      }, 500);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      clearTimeout(submitTimer);
      if (spaceRecognition) {
        try {
          spaceRecognition.stop();
        } catch (error) {
          flashStatus(`Speech recognition stop failed: ${error.message || String(error)}`);
        }
      }
    };
  }, [flashStatus]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInput(val);
    const atMatch = val.match(/@(\w*)$/);
    setShowMentions(!!atMatch);
    if (atMatch) setMentionIndex(0);
  };

  // Auto-resize textarea to fit content, cap at ~5 lines then scroll
  const MAX_HEIGHT = 120;
  const [isMultiLine, setIsMultiLine] = useState(false);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = '24px';
    const scrollH = el.scrollHeight;
    const multiLine = scrollH > 30;
    setIsMultiLine(multiLine);
    if (scrollH > MAX_HEIGHT) {
      el.style.height = MAX_HEIGHT + 'px';
      el.style.overflowY = 'auto';
    } else {
      el.style.height = scrollH + 'px';
      el.style.overflowY = 'hidden';
    }
  }, [input]);

  const insertMention = (agentName) => {
    const before = input.replace(/@\w*$/, '');
    setInput(`${before}@${agentName} `);
    setShowMentions(false);
    setMentionIndex(0);
    inputRef.current?.focus();
  };

  const mentionFilter = input.match(/@(\w*)$/)?.[1]?.toLowerCase() || '';
  const filteredAgents = agents.filter((a) =>
    a.name.toLowerCase().startsWith(mentionFilter)
  );

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    if (agents.length === 0) {
      flashStatus('OpenClaw is not connected.');
      return;
    }
    if (!accessToken) {
      flashStatus('No active Supabase session');
      return;
    }
    setInput('');
    if (inputRef.current) { inputRef.current.style.height = '24px'; inputRef.current.style.overflowY = 'hidden'; }
    setShowMentions(false);
    setLoading(true);

    try {
      const mentionedAgentId = findMentionedAgentId(text, agents);
      const plan = await orchestrateMasterChat(text, mentionedAgentId);
      if (!plan?.assignments?.length) {
        throw new Error('Master chat planner returned no assignments');
      }

      if (plan.mode === 'task') {
        const assignments = plan.assignments;
        const assignedAgents = new Set();

        for (const assignment of assignments) {
          useWorldStore.getState().enqueueTask(
            assignment.agent_id,
            assignment.instruction,
            assignment.label
          );
          useWorldStore.getState().setAgentIndicator(assignment.agent_id, { type: 'task', pulsing: true });
          assignedAgents.add(assignment.agent_id);

          useWorldStore.getState().addTaskToast(
            `${assignment.agent_name} → ${assignment.label}`,
            agents.find((agent) => agent.id === assignment.agent_id)?.color
          );
        }

        for (const aid of assignedAgents) {
          setTimeout(() => {
            void runAgentQueue(aid).catch((error) => {
              flashStatus(`Task failed: ${getErrorMessage(error, 'OpenClaw task failed')}`);
            });
          }, 100);
        }
      } else {
        const [assignment] = plan.assignments;
        if (!assignment?.agent_id) {
          throw new Error('Master chat planner returned no chat target');
        }

        // Canonical chat-mode UX: open the chat-app embed for the routed agent.
        // The user re-engages inside the embed; MasterChat does not stream the reply itself.
        useWorldStore.getState().openChat(assignment.agent_id);
        useWorldStore.getState().setAgentIndicator(assignment.agent_id, { type: 'chat', pulsing: true });
        setTimeout(() => useWorldStore.getState().clearAgentIndicator(assignment.agent_id), 3000);
      }
    } catch (err) {
      const detail = showErrorToast({
        id: 'master-chat-error',
        message: `Master chat failed: ${getErrorMessage(err, 'Something went wrong')}`,
      });
      flashStatus(detail);
    }

    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      width: 480, maxWidth: '90vw', zIndex: 90,
      display: 'flex', flexDirection: 'column', alignItems: 'stretch',
    }}>
      {/* Status flash */}
      {status && (
        <div style={{
          textAlign: 'center', marginBottom: 6,
          ...mono, fontSize: 10, color: 'rgba(255,255,255,0.5)',
          animation: 'fadeIn 0.2s ease-out',
        }}>
          {status}
        </div>
      )}

      {/* @ mention autocomplete */}
      {showMentions && filteredAgents.length > 0 && (
        <div style={{
          background: 'rgba(6,6,7,0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--color-border)',
          borderRadius: 12, marginBottom: 4, overflow: 'hidden',
          boxShadow: 'var(--shadow-elevated)',
        }}>
          {filteredAgents.map((a, i) => (
            <div key={a.id}
              onClick={() => insertMention(a.name)}
              onMouseEnter={() => setMentionIndex(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 12px', cursor: 'pointer',
                background: i === mentionIndex ? 'rgba(255,255,255,0.06)' : 'transparent',
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: a.color }} />
              <span style={{ ...mono, fontSize: 11, color: 'white' }}>{a.name}</span>
              <span style={{ ...mono, fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>{a.role}</span>
            </div>
          ))}
        </div>
      )}

      {/* Mic toggle (floating above input) */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
        <button
          onClick={toggleVoiceMode}
          title={voiceMode === 'open' ? 'Mic on (click to mute)' : isListening ? 'Listening... (release Space)' : 'Mic muted (click for open mic, hold Space to talk)'}
          style={{
            width: 36, height: 36, borderRadius: 18,
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            border: 'none',
            color: voiceMode === 'open' ? '#4ade80' : isListening ? '#fbbf24' : 'rgba(255,255,255,0.25)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'inset 0px 1px 1px rgba(255,255,255,0.06)',
            transition: 'color 0.15s',
            animation: (voiceMode === 'open' && isSpeaking) || isListening ? 'voicePulse 0.5s ease-in-out infinite' : 'none',
          }}
          onMouseEnter={(e) => { if (voiceMode !== 'open' && !isListening) e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
          onMouseLeave={(e) => { if (voiceMode !== 'open' && !isListening) e.currentTarget.style.color = 'rgba(255,255,255,0.25)'; }}
        >
          {voiceMode === 'open' || isListening ? <MicIcon size={16} /> : <MicOffIcon size={16} />}
        </button>
      </div>

      {/* Input bar */}
      <div style={{
        display: 'flex', alignItems: isMultiLine ? 'flex-end' : 'center', gap: 2,
        background: 'rgba(255,255,255,0.07)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0)',
        borderRadius: 20,
        paddingLeft: 12,
        paddingTop: isMultiLine ? 8 : 0,
        paddingBottom: isMultiLine ? 4 : 0,
        boxShadow: 'inset 0px 1px 1px rgba(255,255,255,0.1)',
        transition: 'border-radius 0.15s, padding 0.15s',
      }}>
        {loading && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
            background: 'rgba(251,191,36,0.1)',
            border: '1px solid rgba(251,191,36,0.3)',
            borderRadius: 12, padding: '2px 8px',
            animation: 'processingPulse 1.5s ease-in-out infinite',
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#fbbf24',
              animation: 'indicatorPulse 0.8s ease-in-out infinite',
            }} />
            <span style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 9, color: '#fbbf24' }}>
              Processing...
            </span>
          </div>
        )}

        {isListening && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
            background: isSpeaking ? 'rgba(74,222,128,0.1)' : 'rgba(251,191,36,0.1)',
            border: `1px solid ${isSpeaking ? 'rgba(74,222,128,0.3)' : 'rgba(251,191,36,0.3)'}`,
            borderRadius: 12, padding: '2px 8px',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%',
              background: isSpeaking ? '#4ade80' : '#fbbf24',
              animation: isSpeaking ? 'voicePulse 0.5s ease-in-out infinite' : 'voicePulse 2s ease-in-out infinite',
            }} />
            <span style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 9, color: isSpeaking ? '#4ade80' : '#fbbf24' }}>
              {isSpeaking ? 'Listening...' : 'Waiting...'}
            </span>
          </div>
        )}

        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (showMentions && filteredAgents.length > 0) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex((i) => Math.min(i + 1, filteredAgents.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(filteredAgents[mentionIndex].name);
              } else if (e.key === 'Escape') {
                setShowMentions(false);
              }
            } else if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          rows={1}
          placeholder="Say anything..."
          style={{
            flex: 1, height: 24, background: 'none', border: 'none',
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", fontSize: 14,
            color: 'var(--color-form-field-foreground)', outline: 'none',
            resize: 'none', overflowY: 'hidden', lineHeight: '20px',
            padding: '2px 0',
          }}
        />

        <button data-voice-submit onClick={handleSend} disabled={loading || !input.trim()} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '8px 12px', height: 36,
          background: 'none', border: 'none',
          color: input.trim() ? '#fcfcfc' : 'rgba(163,163,163,0.7)',
          cursor: input.trim() ? 'pointer' : 'default',
          transition: 'color 0.15s',
          flexShrink: 0,
        }}>
          <SendIcon size={16} />
        </button>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes voicePulse { 0%,100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.4); opacity: 1; } }
        @keyframes indicatorPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.6; }
        }
        @keyframes processingPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
